import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Backdrop,
  Box,
  Container,
  CircularProgress,
  Grid,
} from '../../../components/mui/Core/index'
import GenericNav from '../../../components/genericcomponents/navbars/GenericNav'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import TableSearch from '../../../components/TableSearch/table-search'
import { fetchItemTags } from './item-tag-slice'
import { fetchItemCodeSummary } from '../ItemCodeSummary/item-code-slice'
import ItemDetailsCard from '../components/ItemDetailsCard/item-details-card'
import { ItemTagTable } from './components/ItemTagTable/item-tag-table'
import { fetchOrderAllocationsSummary } from '../OrderAllocations/order-allocations-summary-slice'
import { fetchItemSpecialPricing } from '../ItemSpecialPricing/item-special-pricing-slice'
import OrderAllocationsSummaryTable from './components/OrderAllocationsTable/order-allocations-summary-table'
import ItemCodeSummaryTable from './components/ItemCodeSummaryTable/item-code-summary-table'
import SpecialPricingSummaryTable from './components/SpecialPricingSummaryTable/special-pricing-summary-table'
import useBrowserTabTitles from '../../../hooks/browserTabTitles/browser-tab-titles'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import { sessionStorageKeyConstants } from '../../../constants/session-storage-key-constants'

function ItemDetails() {
  const selectedClient = JSON.parse(
    sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT),
  )
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const dmsUser = selectedClient?.dmsFlag || authenticatedUser?.dms
  const dispatch = useDispatch()
  const selectedInventoryItem = useSelector((state) => state.inventory.selectedInventoryItem)
  const fetchItemTagsError = useSelector((state) => state.itemTag.fetchItemTagsError)
  const fetchItemCodeSummaryError = useSelector(
    (state) => state.itemCode.fetchingItemCodeSummaryError,
  )
  const fetchOrderAllocationsSummaryError = useSelector(
    (state) => state.orderAllocationsSummary.fetchingOrderAllocationsSummaryError,
  )
  const fetchItemSpecialPricingSummaryError = useSelector(
    (state) => state.itemSpecialPricing.fetchingItemSpecialPricingSummaryError,
  )
  const [isLoading, setIsLoading] = useState(false)
  const pageTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.inventory.NDC_ITEM} ${selectedInventoryItem?.itemNumber}`
    : `${Constants.inventory.NDC_ITEM} ${selectedInventoryItem?.itemNumber}`

  const searchByOptions = [
    { id: 'tag', text: Constants.itemTagTable.TAG, type: 'string' },
    { id: 'lot', text: Constants.itemTagTable.LOT_NUMBER, type: 'string' },
    { id: 'serialNumbers', text: Constants.itemTagTable.SERIAL_NUMBER, type: 'string' },
    { id: 'expirationDate', text: Constants.itemTagTable.EXPIRATION_DATE, type: 'date' },
    { id: 'warehouse', text: Constants.itemTagTable.WAREHOUSE, type: 'string' },
    { id: 'itemCode', text: Constants.itemTagTable.ITEM_CODE, type: 'string' },
  ]
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)
  const browserTabTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.itemDetails.NDC_ITEM_NUMBER} ${selectedInventoryItem?.itemNumber}`
    : `${Constants.itemDetails.NDC_ITEM_NUMBER} ${selectedInventoryItem?.itemNumber}`
  useBrowserTabTitles(browserTabTitle)

  useEffect(() => {
    /* Dispatch actions to fetch all data to be
       displayed in the item details components */
    async function getAllItemDetailsData() {
      if (selectedInventoryItem) {
        setIsLoading(true)
        await Promise.all([
          dispatch(fetchItemTags(selectedInventoryItem?.itemNumber)),
          dispatch(fetchItemCodeSummary(selectedInventoryItem?.itemNumber)),
          dispatch(fetchOrderAllocationsSummary(selectedInventoryItem?.itemNumber)),
          dispatch(fetchItemSpecialPricing(selectedInventoryItem?.itemNumber)),
        ])
      }
    }
    getAllItemDetailsData()
      .then(() => setIsLoading(false))
      .catch((exception) => {
        setIsLoading(false)
        dispatch(
          setSnackbar({
            message: JSON.stringify(exception),
            severity: 'error',
            action: true,
          }),
        )
      })
  }, [dispatch, selectedInventoryItem])

  useEffect(() => {
    /* When error occurs, display snackbar */
    if (fetchItemTagsError) {
      dispatch(
        setSnackbar({
          message: fetchItemTagsError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
    if (fetchItemCodeSummaryError) {
      dispatch(
        setSnackbar({
          message: fetchItemCodeSummaryError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
    if (fetchOrderAllocationsSummaryError) {
      dispatch(
        setSnackbar({
          message: fetchOrderAllocationsSummaryError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
    if (fetchItemSpecialPricingSummaryError) {
      dispatch(
        setSnackbar({
          message: fetchItemSpecialPricingSummaryError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
  }, [
    fetchItemTagsError,
    fetchItemCodeSummaryError,
    fetchOrderAllocationsSummaryError,
    fetchItemSpecialPricingSummaryError,
    dispatch,
  ])

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValue(inputValue)
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }

  const responsiveStyle = { flexGrow: 1 }
  const sideColumnStyle = { paddingTop: '4px !important' }

  return (
    <Box sx={responsiveStyle}>
      <GenericNav selected='Inventory' user='authenticated' />
      <Backdrop open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl'>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <PageTitle gridSize={12} title={pageTitle} />
          </Grid>
          <Grid size={{ xs: 8.5, lg: 9 }}>
            <ItemDetailsCard />
            <Grid sx={{ mt: 3 }} display='flex' justifyContent='right'>
              <TableSearch returnSearch={returnSearch} searchByOptions={searchByOptions} />
            </Grid>
            <ItemTagTable
              filterBy={tableFilterBy}
              filterValue={tableFilterValue}
              searchByOptions={searchByOptions}
            />
          </Grid>
          <Grid size={{ xs: 3.5, lg: 3 }} sx={sideColumnStyle}>
            <ItemCodeSummaryTable />
            {dmsUser === 'Y' && <SpecialPricingSummaryTable />}
            <OrderAllocationsSummaryTable />
          </Grid>
        </Grid>
      </Container>
      <GenericSnackbar />
    </Box>
  )
}

export default ItemDetails
